import { useMemo } from "react";
import { LANES, minutes, type Span, type Trace as TraceData } from "../lib/trace";

const ROW = 34;
const LABEL_W = 88;
const AXIS_H = 22;

const FILL: Record<Span["kind"], string> = {
  leg: "fill-neutral-300 dark:fill-neutral-600",
  step: "fill-sky-400 dark:fill-sky-600",
  bot: "fill-violet-400 dark:fill-violet-600",
  tool: "fill-teal-400 dark:fill-teal-600",
  queue: "fill-amber-300 dark:fill-amber-600",
  handoff: "fill-transparent stroke-amber-600 dark:stroke-amber-400 [stroke-dasharray:4_3]",
  agent: "fill-emerald-400 dark:fill-emerald-600",
  acw: "fill-emerald-200 dark:fill-emerald-900",
};

/**
 * requirements.md 8.5: the trace. Lanes down, time across; every span is
 * a button with its label and detail as its name, so a keyboard reaches
 * the same thing a mouse does, and the list beneath the chart carries
 * the same content for anyone who prefers it read out. The playhead is
 * the recording's position, translated onto the axis.
 */
export function Trace({
  data,
  width = 960,
  selected,
  onSelect,
  playheadMs,
}: {
  data: TraceData;
  width?: number;
  selected?: Span | null;
  onSelect?: (s: Span) => void;
  /** Recording position, in ms from the recording's start; drawn from `data.recordingOrigin`. */
  playheadMs?: number;
}) {
  const height = AXIS_H + LANES.length * ROW + 6;
  const plot = width - LABEL_W - 12;
  const dur = Math.max(1, data.t1 - data.t0);
  const x = (t: number) => LABEL_W + ((t - data.t0) / dur) * plot;
  // A step of a few hundred milliseconds must still be a target you can hit.
  const minW = plot * 0.006;
  const ticks = useMemo(() => tickTimes(data.t0, data.t1), [data.t0, data.t1]);
  const lanesUsed = LANES.filter((l) => data.spans.some((s) => s.lane === l));
  if (data.spans.length === 0) return <p className="text-sm text-neutral-600 dark:text-neutral-400">Nothing on the timeline yet.</p>;

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="h-auto w-full min-w-[640px]" role="group" aria-label={`Contact timeline: ${data.spans.length} spans over ${minutes(dur)}`}>
        {ticks.map((t) => (
          <g key={t} className="text-neutral-400">
            <line x1={x(t)} x2={x(t)} y1={AXIS_H - 4} y2={height} className="stroke-neutral-200 dark:stroke-neutral-800" strokeWidth={1} />
            <text x={x(t)} y={AXIS_H - 8} fontSize={10} textAnchor="middle" fill="currentColor">{minutes(t - data.t0)}</text>
          </g>
        ))}
        {LANES.map((lane, i) => (
          <g key={lane} className={lanesUsed.includes(lane) ? "text-neutral-700 dark:text-neutral-300" : "text-neutral-400"}>
            <text x={LABEL_W - 8} y={AXIS_H + i * ROW + ROW / 2} dy="0.35em" fontSize={11} textAnchor="end" fill="currentColor">{lane}</text>
          </g>
        ))}
        {data.spans.map((s) => {
          const x0 = x(s.start);
          const w = Math.max(minW, x(s.end) - x0);
          const y = AXIS_H + LANES.indexOf(s.lane) * ROW + (s.kind === "handoff" ? 3 : 6);
          const h = s.kind === "handoff" ? ROW - 6 : ROW - 12;
          const active = selected?.id === s.id;
          return (
            <g
              key={s.id}
              role={onSelect ? "button" : undefined}
              tabIndex={onSelect ? 0 : undefined}
              aria-label={`${s.label}: ${s.detail}${s.error ? " (problem)" : ""}`}
              aria-pressed={onSelect ? active : undefined}
              className={onSelect ? "cursor-pointer outline-none focus-visible:[&>rect]:stroke-accent-strong focus-visible:[&>rect]:stroke-2" : undefined}
              onClick={() => onSelect?.(s)}
              onKeyDown={(ev) => { if (onSelect && (ev.key === "Enter" || ev.key === " ")) { ev.preventDefault(); onSelect(s); } }}
            >
              <rect x={x0} y={y} width={w} height={h} rx={3} className={`${FILL[s.kind]} ${s.error ? "stroke-red-600 dark:stroke-red-400" : ""} ${active ? "stroke-accent-strong" : ""}`} strokeWidth={s.error || active ? 2 : s.kind === "handoff" ? 1.5 : 0} />
              {w > 40 && <text x={x0 + 4} y={y + h / 2} dy="0.35em" fontSize={10} className="pointer-events-none fill-neutral-900 dark:fill-neutral-100">{clip(s.label, w)}</text>}
            </g>
          );
        })}
        {playheadMs !== undefined && data.recordingOrigin !== undefined && (
          <line x1={x(data.recordingOrigin + playheadMs)} x2={x(data.recordingOrigin + playheadMs)} y1={AXIS_H - 2} y2={height} className="stroke-accent-strong" strokeWidth={2} aria-hidden="true" />
        )}
      </svg>
      <ol className="sr-only">
        {data.spans.map((s) => <li key={s.id}>{s.lane}: {s.label}, {s.detail}, from {minutes(s.start - data.t0)} to {minutes(s.end - data.t0)}</li>)}
      </ol>
    </div>
  );
}

function clip(label: string, w: number): string {
  const max = Math.max(3, Math.floor((w - 8) / 5.6));
  return label.length > max ? `${label.slice(0, max - 1)}…` : label;
}

/** Five to eight round ticks across the span: 1s, 5s, 10s, 30s, 1m, 2m, 5m, 10m, 30m, 1h. */
function tickTimes(t0: number, t1: number): number[] {
  const dur = t1 - t0;
  const step = [1000, 5000, 10_000, 30_000, 60_000, 120_000, 300_000, 600_000, 1_800_000, 3_600_000].find((s) => dur / s <= 8) ?? 3_600_000;
  const out: number[] = [];
  for (let t = t0; t <= t1; t += step) out.push(t);
  return out;
}
